import type { SignatureModalValue } from '../components/signature/SignatureModal';
import { publicSupabase } from '../lib/supabaseClient';
import { getSupabaseClient } from './supabase';

export const TURNOVER_STATEMENT = 'I certify on behalf of ITA that all listed items are installed, completed-work photos and drawings/as-builts are provided or explicitly skipped in this review, and the system is ready to turn over to the customer under the approved Scope of Work.';
export type CompletionSummary = {
  job_code: string;
  name: string;
  quote_version: number;
  scope_of_work: string;
  approved_amount: number;
  final_total: number;
  acknowledgment_statement: string;
  warranty?: Array<{ name: string; description: string | null; included: boolean; selected: boolean;
    term_months: number; pm_visits: number; pm_frequency_months: number | null; discount_percent: number;
    response: string | null; onsite: string | null; manufacturer: string | null; loaner: string | null;
    /** Since 2026-10-13: the length signed (null for a term that isn't whole years) and its price. */
    term_years?: number | null; price?: number }>;
  rooms: Array<{ id: string; name: string }>;
  items: Array<{ id: string; room_id: string | null; description: string; manufacturer: string | null; model: string | null; quantity: number; uom: string }>;
};
export type CompletionFile = {
  bucket?: 'photos' | 'installation-closeout' | 'job-photos';
  source_photo_id?: string;
  path: string; name: string; kind: 'photo' | 'drawing'; room_id: string | null; added_at: string; added_by: string | null;
};
export type CompletionReview = {
  revision: number;
  items_confirmed: boolean;
  photos_status: 'required' | 'skipped';
  drawings_status: 'pending' | 'uploaded' | 'na';
  files: CompletionFile[];
  snapshot: CompletionSummary;
  photos_complete: boolean;
  drawings_complete: boolean;
  internal_signature_id: string | null;
  certified_at: string | null;
  certified_by: string | null;
  closed_at: string | null;
  customer_name: string | null;
  recipient_email: string | null;
  link_expires_at: string | null;
  stage: 'review' | 'ready_for_customer' | 'awaiting_customer' | 'closed';
  updated_at: string | null;
  can_edit: boolean;
  path?: string | null;
};
export const COMPLETION_STAGE_LABELS: Record<CompletionReview['stage'], string> = {
  review: 'Completion review', ready_for_customer: 'Ready for customer', awaiting_customer: 'Awaiting customer', closed: 'Closed',
};

export async function fetchInstallationCompletion(jobId: string): Promise<CompletionReview> {
  const { data, error } = await getSupabaseClient().rpc('get_installation_completion_review', { p_job_id: jobId });
  if (error) throw error;
  return data as CompletionReview;
}

export async function updateInstallationCompletion(jobId: string, revision: number, action: 'items' | 'photos' | 'drawings' | 'add_file' | 'remove_file' | 'certify' | 'reopen' | 'create_link', value: Record<string, unknown> | SignatureModalValue): Promise<CompletionReview> {
  const { data, error } = await getSupabaseClient().rpc('update_installation_completion_review', {
    p_job_id: jobId, p_revision: revision, p_action: action, p_value: value,
  });
  if (error) throw error;
  return data as CompletionReview;
}

export async function uploadCompletionFile(jobId: string, file: File, kind: CompletionFile['kind'], roomId: string | null): Promise<{ path: string; name: string; kind: CompletionFile['kind']; room_id: string | null }> {
  if (!file.size || file.size > 25 * 1024 * 1024) throw new Error('Choose a nonempty file up to 25 MB.');
  if (kind === 'photo' && !file.type.startsWith('image/')) throw new Error('Choose a photo of the completed work.');
  const path = `${jobId}/${kind}/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const { error } = await getSupabaseClient().storage.from('installation-closeout').upload(path, file, {
    contentType: file.type || 'application/octet-stream', upsert: false,
  });
  if (error) throw error;
  return { path, name: file.name, kind, room_id: roomId };
}

export async function getCompletionFileUrl(path: string): Promise<string> {
  const { data, error } = await getSupabaseClient().storage.from('installation-closeout').createSignedUrl(path, 600, { download: true });
  if (error) throw error;
  return data.signedUrl;
}

export async function fetchPublicCompletion(token: string): Promise<{ summary: CompletionSummary; signed_at: string | null }> {
  const { data, error } = await publicSupabase.rpc('get_installation_completion_for_signature', { p_token: token });
  if (error) throw error;
  return data;
}

export async function acceptPublicCompletion(token: string, signature: SignatureModalValue): Promise<{ signed_at: string }> {
  const { data, error } = await publicSupabase.rpc('accept_installation_completion', { p_token: token, p_signature: signature });
  if (error) throw error;
  return data;
}

export async function fetchCompletionStages(jobIds: string[]): Promise<Record<string, string>> {
  const result: Record<string, string> = {};
  for (let index = 0; index < jobIds.length; index += 500) {
    const { data, error } = await getSupabaseClient().rpc('get_installation_completion_stages', { p_job_ids: jobIds.slice(index, index + 500) });
    if (error) throw error;
    Object.assign(result, data);
  }
  return result;
}
